import React from "react";

import { Button } from "~/components/button";
import { FormFieldErrorMessage } from "~/components/form/error-message";
import { FormField } from "~/components/form/field";
import { FormLabel } from "~/components/form/label";
import { IconDelete } from "~/components/icon";
import { Input } from "~/components/input";
import { Tooltip } from "~/components/tooltip";
import { TooltipContent } from "~/components/tooltip/content";
import { TooltipTrigger } from "~/components/tooltip/trigger";
import { cx } from "~/tools/class-variance";
import { useScoped } from "~/tools/impulse";

import { Widget } from "../../../_widget";

import type { StaticValueState } from "./_form-state";

export interface StaticValueProps {
	dragHandle: React.ReactNode;
	onDelete: undefined | VoidFunction;
	state: StaticValueState;
	className?: string;
}

export const StaticValue = React.forwardRef<React.ElementRef<typeof Widget>, StaticValueProps>(
	({ dragHandle, onDelete, state, className }, ref) => {
		const id = useScoped((scope) => state.fields.id(scope), [state]);

		return (
			<Widget card className={cx("grid grid-cols-[max-content_1fr_auto] gap-2", className)} ref={ref}>
				<FormField className="col-span-full grid grid-cols-subgrid" layout="horizontal" state={state.fields.value}>
					<FormLabel>Value</FormLabel>

					{(scope, field) => (
						<Input
							name={`dashboard-list-static-variable-${id}-value`}
							onChange={(event) => field.setInput(event.target.value)}
							size="sm"
							value={field.getInput(scope)}
						/>
					)}

					{dragHandle && (
						<Tooltip disableHoverableContent>
							<TooltipTrigger>{dragHandle}</TooltipTrigger>
							<TooltipContent>Drag to reorder</TooltipContent>
						</Tooltip>
					)}

					<FormFieldErrorMessage className="col-start-2" />
				</FormField>

				<FormField className="col-span-full grid grid-cols-subgrid" layout="horizontal" state={state.fields.label}>
					<FormLabel>Label</FormLabel>

					{(scope, field) => (
						<Input
							name={`dashboard-list-static-variable-${id}-label`}
							onChange={(event) => field.setInput(event.target.value)}
							placeholder={state.fields.value.getOutput(scope) ?? ""}
							size="sm"
							value={field.getInput(scope)}
						/>
					)}

					{onDelete != null && (
						<Button icon onClick={onDelete} size="sm" variant="tertiary">
							<IconDelete className="size-3" />
						</Button>
					)}

					<FormFieldErrorMessage className="col-start-2" />
				</FormField>
			</Widget>
		);
	},
);
